import React, { useCallback, useEffect, useMemo, useReducer, useRef, useState } from 'react'
import { UserDto } from '../../dtos/user'
import { simpleNotification, validateSession } from '../../_utils/utils'
import { useService } from '@pushly/aqe/lib/hooks'
import { AppState, NoDomainsError, UserDependenciesError } from '../../stores/app'
import { SessionState } from '@pushly/aqe'
import { sendGAEvent } from '../../components/ga-helper/ga-helper'
import { AppService, UserService } from '../../services/index'
import { AUTH0_PLATFORM_REDIRECT_PATHNAME, AUTH0_STATE_SESSION_KEY } from '../../constants'
import { getPlatformIdentitySnapshot } from '../platform-identity-provider/platform-identity-provider'
import { buildAuthorizeRoute } from '../../_utils/routing'
import axios from 'axios'
import aqe from '@pushly/aqe'
import { useHistory, useLocation } from 'react-router-dom'
import { parseQueryStringParams } from '../../_utils/query-string'
import { PushlyAuth0State } from '../../components/auth0-redirect/auth-redirect'
import { platformUserReducer } from '@pushly/aqe/lib/providers/user-preferences-provider/internal/reducer'
import { broadcastSessionRestored } from '@pushly/aqe/lib/utils/session/session-state-sync'
import {
    PlatformUserContext as BasePlatformUserContext,
    PlatformUserDispatchContext,
    UserContextAction,
    UserPreferencesContext,
} from '@pushly/aqe/lib/providers/user-preferences-provider/internal'
import { PreferenceService } from '@pushly/aqe/lib/services/preference.service'
import { Container } from 'typescript-ioc'
import { streamApplicationError } from '../../_utils/airbrake'
import { classifyError, type ErrorKind } from '../../core/error-kind'
import { useBoot } from '../../core/boot/boot-provider'
import { MAX_SESSION_RETRIES } from '../../core/boot/boot-machine'
import { bridgeSessionToBoot } from '../../core/boot/session-bridge'
import { startAccessDeadline } from '../../core/boot/access-deadline'
import { AuthorizationRefusedError } from '../../core/boot/authorization-error'
import { BOOT_MARKS, markBootStep } from '../../core/boot/boot-marks'
import { loadFlagCatalog } from '../../core/flags'

export enum UserReviewStatus {
    PENDING = 'pending',
    COMPLETED = 'completed',
    DISMISSED = 'dismissed',
    ACKNOWLEDGED = 'acknowledged',
}

export type PlatformUser =
    | {
          authenticated: false
          revalidate?: boolean
          preferences: UserPreferencesContext
          loading: boolean
      }
    | {
          authenticated: true
          user: UserDto
          revalidate?: boolean
          preferences: UserPreferencesContext
          loading: boolean
          reviewState: {
              status: UserReviewStatus
              url?: string
              loading?: boolean
          }
      }

type UserContext = PlatformUser & { assignUser?: React.Dispatch<React.SetStateAction<PlatformUser>> }

const initialPlatformUser: PlatformUser = {
    authenticated: false,
    preferences: {
        state: {
            bundle: null,
            loading: true,
        },
        refresh: async () => {},
    },
    loading: false,
}
const initialReviewState = { status: UserReviewStatus.PENDING, loading: false }

export const PlatformUserContext = React.createContext<UserContext>(initialPlatformUser)

/**
 * The kind of a failed Auth0 code exchange. A failed exchange must never report an unauthenticated
 * kind: that sends the browser back to Auth0, which returns another code, forever. A server or
 * network fault is worth retrying; a 4xx or a bad parameter is not. That includes 408 and 429: a code may
 * already be consumed by the time the answer is lost, so it is never replayed.
 */
const exchangeFailureKind = (error: unknown): ErrorKind => {
    const status = (error as { response?: { status?: number } })?.response?.status
    if (status !== undefined && status >= 400 && status < 500) {
        return 'misconfigured'
    }

    return classifyError(error) === 'transient' ? 'transient' : 'misconfigured'
}

/**
 * The kind of an authorize URL that carries no usable code. A provider refusal such as `access_denied`
 * is the user's answer, not a fault here. The provider's own outages (`server_error`,
 * `temporarily_unavailable`) cannot be retried, because there is no code to replay, so they stay ours.
 * Without a provider `error` the URL itself is wrong, which is also ours.
 */
const authorizeFailureKind = (providerError: string | undefined): ErrorKind => {
    if (!providerError || providerError === 'server_error' || providerError === 'temporarily_unavailable') {
        return 'misconfigured'
    }

    return 'forbidden'
}

/**
 * A canceled call on the boot path is a load that did not finish, not a decision. Nothing else is
 * going to answer it, so the user retries.
 */
const retryableKind = (kind: ErrorKind): ErrorKind => (kind === 'aborted' ? 'transient' : kind)

/**
 * The kind of a failed session check: a refused session signs in again, an outage retries. A 403 from
 * the session check means the token is not a valid session, because the auth guard answers 403 for a
 * token that does not resolve to a user, a revoked session and a disabled account.
 */
const sessionCheckFailureKind = (error: unknown): ErrorKind => {
    const kind = retryableKind(classifyError(error))
    if (kind === 'unauthenticated' || kind === 'forbidden') {
        return 'unauthenticated'
    }

    return kind === 'transient' ? 'transient' : 'misconfigured'
}

const accessFailureKind = (error: unknown): ErrorKind => {
    if (error instanceof UserDependenciesError) {
        return retryableKind(error.kind)
    }

    return retryableKind(classifyError(error))
}

/** Reads and clears the page a session expiry stored. Storage that cannot be read means there is none. */
const takeRefreshRedirect = (): string | null => {
    try {
        const redirect = localStorage.getItem('refreshRedirectURI')
        localStorage.removeItem('refreshRedirectURI')
        return redirect
    } catch {
        return null
    }
}

/**
 * The domain list, then what the URL opens. A domain the user cannot open is not a failure here: the
 * app mounts and shows the unavailable state. A failed lookup is, and keeps its kind.
 */
const loadDomainAccess = async (appState: AppState, path: string): Promise<void> => {
    await appState.fetchUserDependencies()

    // A resolution still in flight is one an earlier attempt abandoned, so a retry starts its own.
    const owner = appState.accessOwner
    const resolution = await (owner.resolution.status === 'resolving' ? owner.retry() : owner.resolveUrl(path))
    if (resolution.status === 'failed') {
        throw new UserDependenciesError(resolution.kind, resolution.error)
    }
}

const loadAbilities = async (appState: AppState): Promise<void> => {
    await appState.abilityStore.loadAbilitiesForCurrentUser()

    const abilityState = appState.abilityStore.loadState
    if (abilityState.status === 'failed') {
        throw abilityState.error
    }
}

/** A load's outcome as a value, so loads that run in parallel can all be waited for. */
const settle = (load: Promise<void>): Promise<{ error: unknown } | undefined> =>
    load.then(
        () => undefined,
        (error: unknown) => ({ error }),
    )

/**
 * Tier 1 and tier 2 loader: the session check (or the Auth0 code exchange) while boot is in
 * `session`, and the access data while it is in `access`. It reports each outcome to the boot
 * machine and renders its children in every phase; the boot gate below it decides what the user sees.
 */
export const UserProvider: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
    const loc = useLocation()
    const { phase, send, attempt } = useBoot()
    const phaseName = phase.phase

    const appState = useService(AppState)
    const sessionState = useService(SessionState)
    const appService = useService(AppService)
    const preferenceService = Container.get(PreferenceService)
    const userSvc = useService(UserService)

    const history = useHistory()
    appService.attachHistoryContainer(history)

    const [userContext, dispatch] = useReducer(platformUserReducer, initialPlatformUser)

    const [reviewState, setReviewState] = useState<{
        status: UserReviewStatus
        url?: string
        loading?: boolean
    }>(initialReviewState)
    // The user the review state and the preferences bundle belong to. A restore as another user starts both over.
    const reviewUserId = useRef<number | undefined>(undefined)
    const activeUserId = useRef<number | undefined>(undefined)

    const combinedContextValue = useMemo<PlatformUser>(
        () =>
            userContext.authenticated
                ? {
                      ...userContext,
                      reviewState,
                  }
                : {
                      ...userContext,
                  },
        [userContext, reviewState],
    )

    // Set when the access data is loaded. `accessLoaded` is sent from an effect once this has rendered,
    // so the app never mounts before the context it reads (the signed-in user) has been committed.
    const [accessDone, setAccessDone] = useState(false)

    // The session phase hands the user it found to the access phase.
    const sessionUser = useRef<UserDto | undefined>(undefined)
    // Set when the session came from the Auth0 code exchange: where to send the user once boot is done.
    const postAuthorizeRedirect = useRef<{ redirectURL?: string } | undefined>(undefined)
    // The loading overlay count is released once per boot, however many times access is retried.
    const appLoadingReleased = useRef(false)
    // Bumped when a boot attempt starts and when its deadline fires, so a preferences load that an earlier
    // attempt started can tell it was abandoned.
    const bootPreferencesGeneration = useRef(0)
    // The session phase reads the path it entered on; navigation never starts another check.
    const pathname = useRef(loc.pathname)
    pathname.current = loc.pathname

    const assignUser = useCallback(
        (update: React.SetStateAction<PlatformUser>) => {
            if (typeof update === 'function') {
                setReviewState((prev) => {
                    // Note: We only support updating reviewState via assignUser for now
                    // as the rest of the userContext is managed by the reducer.
                    const next = update({ ...combinedContextValue } as PlatformUser)
                    if (next.authenticated) {
                        return next.reviewState
                    }
                    return prev
                })
            } else {
                if (update.authenticated) {
                    setReviewState(update.reviewState)
                }
            }
        },
        [combinedContextValue],
    )

    const releaseAppLoading = useCallback(() => {
        if (!appLoadingReleased.current) {
            appLoadingReleased.current = true
            appService.unsetAppLoading()
        }
    }, [])

    /**
     * Loads the bundle the app renders from. A failure leaves the app on defaults: the context records
     * it in `loadingError` and the user is told. It never fails boot, and it never throws.
     *
     * `isCurrent` lets a caller abandon the load: once it answers false, the answer neither updates the
     * context nor tells the user. A load with no `isCurrent` (the user's own refresh) is never abandoned.
     */
    const loadBootstrapPreferences = useCallback(
        async (targetUserId: number, loadIsCurrent: () => boolean = () => true): Promise<void> => {
            // A bundle is for one user: an answer for a user the session has since changed to is dropped.
            const isCurrent = () => loadIsCurrent() && activeUserId.current === targetUserId
            dispatch({ type: UserContextAction.BOOTSTRAP_START })

            const failed = (message: string) => {
                if (!isCurrent()) {
                    return
                }
                dispatch({ type: UserContextAction.BOOTSTRAP_ERROR, error: message })
                // A failed load is a finished load: readers that wait for the bundle render on defaults rather than
                // waiting forever, and the user's refresh can still ask again.
                dispatch({
                    type: UserContextAction.SET_PREFERENCES,
                    preferences: {
                        state: { bundle: null, loading: false },
                        refresh: () => loadBootstrapPreferences(targetUserId),
                    },
                })
                simpleNotification('error', 'Failed to load user preferences')
            }

            try {
                const response = await preferenceService.fetchBootstrapPreferences(targetUserId, 'pufferfish')

                // The service reports a failed request as `ok: false` and keeps no status or error.
                if (!response.ok) {
                    failed('Failed to load user bootstrap preferences')
                    return
                }

                if (!isCurrent()) {
                    return
                }

                const preferencesContext: UserPreferencesContext = {
                    state: {
                        bundle: response.data,
                        loading: false,
                    },
                    refresh: () => loadBootstrapPreferences(targetUserId),
                }

                dispatch({
                    type: UserContextAction.BOOTSTRAP_SUCCESS,
                    preferences: preferencesContext,
                })
            } catch (error) {
                failed(error?.message || 'Failed to load user bootstrap preferences')
            }
        },
        [],
    )

    const requestG2ReviewFormAccessToken = async (userId: number, isCurrent: () => boolean) => {
        const userReviewState = appService.getG2ReviewStatus()[userId]
        const nextReviewState: { status: UserReviewStatus; url?: string; loading: boolean } = {
            status: userReviewState?.status ?? UserReviewStatus.PENDING,
            loading: false,
        }

        // reset to pending when lua expires
        if (
            userReviewState?.status === UserReviewStatus.DISMISSED &&
            new Date() >= new Date(userReviewState.lua + 7 * 24 * 60 * 60 * 1000)
        ) {
            nextReviewState.status = UserReviewStatus.PENDING
        }

        if (userReviewState?.status !== UserReviewStatus.COMPLETED) {
            try {
                const { data } = await userSvc.getG2ReviewEmbedLink(userId)

                if (data.status !== UserReviewStatus.COMPLETED) {
                    nextReviewState.url = data.url
                } else {
                    nextReviewState.status = UserReviewStatus.COMPLETED
                }
            } catch (error) {
                console.debug('Error requesting G2 review form access token: ', { error })
            }
        }

        // An answer for a user the session no longer belongs to is dropped; the embed URL is tokenized to its user.
        if (isCurrent()) {
            setReviewState(nextReviewState)
        }
    }

    // The review state belongs to a user id, not to being authenticated: a restore as another user keeps the
    // session authenticated, and must not carry the previous user's embed URL.
    const authenticatedUserId = userContext.authenticated ? userContext.user.id : undefined
    React.useEffect(() => {
        if (authenticatedUserId === undefined) {
            return
        }

        let current = true
        void requestG2ReviewFormAccessToken(authenticatedUserId, () => current)
        return () => {
            current = false
        }
    }, [authenticatedUserId])

    // Tier 1: the Auth0 code exchange on the authorize path, otherwise the session check. The effect
    // starts once per entry to `session`; `attempt` starts the next transient retry. Navigation does
    // not start one.
    useEffect(() => {
        if (phaseName !== 'session') {
            return
        }

        let superseded = false
        // The code exchange has been sent and not answered.
        let exchanging = false

        const failed = (kind: ErrorKind, error: unknown) => {
            if (superseded) {
                return
            }

            if (kind === 'unauthenticated') {
                // Do not set isAuthenticated to false if this is a session expiration.
                // We want to keep the user as "authenticated but expired" so the session
                // expiration screen shows instead of redirecting to login.
                if (!sessionState.sessionExpired) {
                    sessionState.isAuthenticated = false
                }
                releaseAppLoading()
            } else if (kind === 'forbidden') {
                // The identity provider's answer to the user is not a fault to report.
            } else if (kind !== 'transient' || attempt >= MAX_SESSION_RETRIES) {
                streamApplicationError(error, { tier: 'session', kind })
            }

            send({ type: 'sessionFailed', kind, error })
        }

        const validated = (user: UserDto) => {
            if (superseded) {
                return
            }

            sessionUser.current = user
            send({ type: 'sessionValidated' })
        }

        // The code is single use, so a failed exchange that is not retried with it leaves the authorize
        // URL. Retry or Reload then checks the session normally, and with no session signs in again.
        const failedExchange = (kind: ErrorKind, error: unknown) => {
            if (kind !== 'transient' && !superseded) {
                history.replace('/')
            }
            failed(kind, error)
        }

        const exchangeCode = async () => {
            const params = parseQueryStringParams()
            const code = params.get('code')?.trim()
            const state = params.get('state')?.trim()
            const stateSessionValue = window.sessionStorage.getItem(AUTH0_STATE_SESSION_KEY)

            if (!code || state !== stateSessionValue) {
                // The provider's own reason is read before the URL is cleaned, and it travels with the failure.
                const providerError = params.get('error')?.trim() || undefined
                failedExchange(
                    authorizeFailureKind(providerError),
                    new AuthorizationRefusedError(providerError, params.get('error_description') ?? undefined),
                )
                return
            }

            let stateVal: PushlyAuth0State
            try {
                stateVal = JSON.parse(atob(state))
            } catch (error) {
                failedExchange('misconfigured', error)
                return
            }

            // Clear session expired flag when starting token exchange
            // This allows a fresh login after session expiration
            if (sessionState.sessionExpired) {
                sessionState.sessionExpired = false
            }

            // Clear logout flag when starting new auth flow
            if (appState.isLoggingOut) {
                appState.isLoggingOut = false
            }

            const identity = getPlatformIdentitySnapshot()
            if (!identity.loaded) {
                failedExchange('misconfigured', new Error('IDP not available'))
                return
            }

            const label = identity.whiteLabelSettings
            const query = [
                `code=${code}`,
                `redirect_uri=${buildAuthorizeRoute(label.getHostname())}`,
                `tenant_id=${label.getAuth0TenantId()}`,
                `client_id=${label.getAuth0PlatformClientId()}`,
            ].join('&')

            try {
                exchanging = true
                const res = await axios.get<any>(`${aqe.defaults.publicApiDomain}/auth/token?${query}`, {
                    bypassGlobalErrorHandlers: true,
                } as any)
                exchanging = false

                if (res.status !== 200) {
                    throw new Error(`Unexpected status from the code exchange: ${res.status}`)
                }

                // An answer that arrives after the attempt was abandoned changes nothing: the deadline already
                // consumed the state and left the authorize URL.
                if (superseded) {
                    return
                }

                window.sessionStorage.removeItem(AUTH0_STATE_SESSION_KEY)
                postAuthorizeRedirect.current = { redirectURL: stateVal.redirectURL }
                validated(UserDto.fromApiResponse(res.data.data))
            } catch (error) {
                exchanging = false
                if (superseded) {
                    return
                }
                const kind = exchangeFailureKind(error)
                // A transient failure is retried with the same code and state, so they are kept.
                if (kind !== 'transient') {
                    window.sessionStorage.removeItem(AUTH0_STATE_SESSION_KEY)
                }
                failedExchange(kind, error)
            }
        }

        const checkSession = async () => {
            try {
                validated(UserDto.fromApiResponse(await validateSession()))
            } catch (error) {
                failed(sessionCheckFailureKind(error), error)
            }
        }

        const run = async () => {
            try {
                await appState.initializer
            } catch (error) {
                failed(classifyError(error), error)
                return
            }

            if (superseded) {
                return
            }

            if (pathname.current === AUTH0_PLATFORM_REDIRECT_PATHNAME) {
                await exchangeCode()
            } else {
                await checkSession()
            }
        }

        // The session check and the code exchange answer within the same deadline as the access load. A
        // late answer changes nothing, and the machine retries a transient failure.
        // A code whose answer was not seen may already be consumed, so a deadline during the exchange
        // leaves the authorize URL. The retry then checks the session, which finds the one the exchange
        // may have started, instead of sending the code a second time.
        const deadline = startAccessDeadline((error) => {
            if (exchanging) {
                window.sessionStorage.removeItem(AUTH0_STATE_SESSION_KEY)
                history.replace('/')
            }
            failed('transient', error)
            superseded = true
        }, 'session')

        run()
            .catch((error) => failed(sessionCheckFailureKind(error), error))
            .finally(deadline.cancel)

        return () => {
            superseded = true
            deadline.cancel()
        }
    }, [phaseName, attempt])

    // Tier 2: everything the app needs before it renders, then the first route after a code exchange.
    useEffect(() => {
        if (phaseName !== 'access') {
            return
        }

        let superseded = false
        const user = sessionUser.current!
        const generation = ++bootPreferencesGeneration.current

        const fail = (kind: ErrorKind, error: unknown) => {
            if (!superseded) {
                // A user with no domains is an expected outcome with its own screen; it is not reported.
                if (!(error instanceof NoDomainsError)) {
                    streamApplicationError(error, { tier: 'access', kind })
                }
                if (kind === 'unauthenticated') {
                    // The session ended after it was validated. Sign in again, as the session phase does.
                    if (!sessionState.sessionExpired) {
                        sessionState.isAuthenticated = false
                    }
                    releaseAppLoading()
                }
                send({ type: 'accessFailed', kind, error })
            }
        }

        // The deadline covers the whole load. Once it has fired the load is superseded, so a late answer
        // changes nothing.
        const deadline = startAccessDeadline((error) => {
            fail('transient', error)
            superseded = true
            bootPreferencesGeneration.current++
            // A domain list still in flight is abandoned so Retry asks again instead of waiting on it.
            appState.abandonUserDependencies()
        })

        const run = async () => {
            sendGAEvent('set_current_user', {}, appState)

            // The domain access, the abilities, the flag catalog and the preferences bundle do not depend on each other.
            const previousUser = appState.currentUser
            appState.currentUser = user
            // The owner keys the last-used domain by user, so it learns the user before it resolves the URL. A
            // different user than the one loaded makes it forget everything the previous one had open.
            appState.accessOwner.setUser(user.id)
            // Abilities that belong to another user, or a load an earlier attempt abandoned, are dropped so the
            // load below asks again.
            if (previousUser && (previousUser.id !== user.id || appState.abilityStore.loadState.status !== 'ready')) {
                appState.abilityStore.flush()
            }
            markBootStep(BOOT_MARKS.userLoaded)
            // No user reads another user's bundle. The reducer keeps the previous bundle through BOOTSTRAP_START,
            // so a different user than the one loaded starts from the initial preferences until their own answer lands.
            if (activeUserId.current !== undefined && activeUserId.current !== user.id) {
                dispatch({ type: UserContextAction.SET_PREFERENCES, preferences: initialPlatformUser.preferences })
            }
            activeUserId.current = user.id
            // Preferences never gate boot: the app opens on defaults and takes the bundle when it arrives.
            void loadBootstrapPreferences(user.id, () => bootPreferencesGeneration.current === generation)
            const [accessFailure, abilitiesFailure, flagsFailure] = await Promise.all([
                settle(loadDomainAccess(appState, pathname.current)),
                settle(loadAbilities(appState)),
                settle(loadFlagCatalog()),
            ])
            const requiredFailure = accessFailure ?? abilitiesFailure ?? flagsFailure
            if (requiredFailure) {
                fail(accessFailureKind(requiredFailure.error), requiredFailure.error)
                return
            }

            if (superseded) {
                return
            }

            const userReviewStatus = appService.getG2ReviewStatus()[user.id]
            // Applied in the same update as the new user, so no render pairs this user with another user's URL.
            const sameUser = reviewUserId.current === undefined || reviewUserId.current === user.id
            reviewUserId.current = user.id
            setReviewState((prev) => ({
                ...(sameUser ? prev : initialReviewState),
                status: userReviewStatus?.status ?? 'pending',
            }))
            dispatch({
                type: UserContextAction.SET_AUTHENTICATED_USER,
                user,
                currentUserDomainIds: appState.currentUserDomains?.map((domain) => domain.id) ?? [],
            })

            if (!sessionState.isAuthenticated) {
                // Only call startSession() on initial login (not on page refresh or
                // context re-initialization). startSession() clears all session timestamps
                // and localStorage sync state, so calling it on refresh would wipe the
                // valid session state that the FSM orchestrator needs to resume correctly.
                // broadcastSessionRestored() tells other tabs stuck on the expired
                // interstitial to reload and pick up the new session.
                appService.startSession()
                broadcastSessionRestored()
            }
            sessionState.isAuthenticated = true
            releaseAppLoading()

            const authorized = postAuthorizeRedirect.current
            postAuthorizeRedirect.current = undefined
            if (authorized) {
                const redirect = takeRefreshRedirect()

                if (redirect) {
                    appService.route(redirect)
                } else {
                    appService.routeWithinDomain('/dashboard')
                }

                if (authorized.redirectURL) {
                    console.debug('Redirecting user to: ', authorized.redirectURL)
                    history.replace(authorized.redirectURL)
                }
            }

            setAccessDone(true)
        }

        run()
            .catch((error) => fail(accessFailureKind(error), error))
            .finally(deadline.cancel)

        return () => {
            superseded = true
            deadline.cancel()
        }
    }, [phaseName])

    useEffect(() => {
        if (phaseName === 'access' && accessDone) {
            setAccessDone(false)
            send({ type: 'accessLoaded' })
        }
    }, [phaseName, accessDone])

    // Session facts that arrive after boot (expiry, restore, logout) move the machine. A new bridge per
    // phase reads the current state, so a change made during an earlier phase is not missed.
    useEffect(() => bridgeSessionToBoot({ sessionState, appState, phase: phaseName, send }), [phaseName])

    return (
        <BasePlatformUserContext.Provider value={userContext}>
            <PlatformUserContext.Provider value={{ ...combinedContextValue, assignUser }}>
                <PlatformUserDispatchContext.Provider value={dispatch}>{children}</PlatformUserDispatchContext.Provider>
            </PlatformUserContext.Provider>
        </BasePlatformUserContext.Provider>
    )
}
